import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

const SIZES = ["Solo", "2-10", "11-50", "51-200", "200+"];
const COUNTS = [10, 25, 50];

export default function NewList() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    business_name: "",
    size: "Solo",
    industry: "",
    offering: "",
    ideal_customer: "",
    location: "",
    count: 10,
    client_email: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isFree = (user?.plan ?? "free") === "free";
  const MONTHLY_PAID = ["plan_starter", "plan_growth", "plan_scale"];
  const isMonthlyPaid =
    MONTHLY_PAID.includes(user?.plan) &&
    user?.subscription_interval === "month" &&
    ["active", "trialing"].includes(user?.subscription_status);
  const defaultClientEmail = (user?.client_email_default || "").trim();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/lead-lists", form);
      await refresh();
      toast.success("Generation started");
      nav(`/lists/${data.id}/progress`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="overline text-primary">Step 1 of 1</div>
        <h1 className="mt-2 text-4xl font-medium tracking-tighter">New lead list</h1>
        <p className="mt-3 text-muted-foreground text-sm">
          Tell us about your business and ideal buyer. We&apos;ll do the research.
        </p>

        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="new-list-form">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="business_name">Business name</Label>
              <Input id="business_name" required value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="input-business-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Input id="industry" required placeholder="e.g. SaaS, Logistics…" value={form.industry} onChange={(e) => set("industry", e.target.value)} data-testid="input-industry" />
            </div>
          </div>

          <div className="space-y-3">
            <Label>Business size</Label>
            <RadioGroup
              value={form.size}
              onValueChange={(v) => set("size", v)}
              className="grid grid-cols-2 sm:grid-cols-5 gap-2"
              data-testid="radio-size"
            >
              {SIZES.map((s) => (
                <Label
                  key={s}
                  htmlFor={`size-${s}`}
                  className={`cursor-pointer rounded-lg border px-3 py-3 text-sm text-center transition-colors ${
                    form.size === s ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <RadioGroupItem id={`size-${s}`} value={s} className="sr-only" />
                  {s}
                </Label>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            <Label htmlFor="offering">What you sell / offer</Label>
            <Textarea id="offering" required rows={3} placeholder="e.g. Route-optimization software for mid-market logistics companies"
              value={form.offering} onChange={(e) => set("offering", e.target.value)} data-testid="input-offering" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ideal_customer">Ideal customer profile <span className="text-muted-foreground">(optional)</span></Label>
            <Textarea id="ideal_customer" rows={2} placeholder="e.g. Operations leaders at 50-500 employee logistics companies"
              value={form.ideal_customer} onChange={(e) => set("ideal_customer", e.target.value)} data-testid="input-icp" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="location">Target location</Label>
              <Input id="location" required placeholder="e.g. Canada, San Francisco Bay Area…" value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="input-location" />
            </div>
            <div className="space-y-2">
              <Label>Number of leads</Label>
              <Select value={String(form.count)} onValueChange={(v) => set("count", parseInt(v, 10))}>
                <SelectTrigger data-testid="select-count"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COUNTS.map((c) => (
                    <SelectItem key={c} value={String(c)} disabled={isFree && c > 10} data-testid={`count-${c}`}>
                      {c}{isFree && c > 10 ? " — upgrade required" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {isMonthlyPaid && (
            <div className="space-y-2 rounded-xl border border-primary/20 bg-primary/5 p-5" data-testid="per-list-client-email-block">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="client_email" className="flex items-center gap-2">
                  Client email for Monday digest <span className="text-muted-foreground">(optional)</span>
                </Label>
                {defaultClientEmail && (
                  <span className="text-[11px] text-muted-foreground">
                    Default: <span className="text-foreground">{defaultClientEmail}</span>
                  </span>
                )}
              </div>
              <Input
                id="client_email"
                type="email"
                placeholder={defaultClientEmail || "client@theircompany.com"}
                value={form.client_email}
                onChange={(e) => set("client_email", e.target.value)}
                data-testid="input-client-email"
              />
              <div className="text-xs text-muted-foreground">
                Every Monday 09:00 UTC we'll send this list's new leads to this inbox. Leave blank to use your default.
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="text-xs text-muted-foreground max-w-md">
              This will deduct 1 credit. AI-generated; verify before contacting.
            </div>
            <Button type="submit" disabled={loading} className="rounded-full bg-primary hover:bg-[#FF7043] text-white h-11 px-7" data-testid="submit-new-list">
              {loading ? "Starting…" : "Generate leads"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
